import { createSelector } from 'reselect';

import { createParameterSelector, OperationStatus } from '@pressreader/appstore';
import { selectUserIsAuthenticated } from '@pressreader/authentication-core';
import type { IArticle } from '@pressreader/content-types';

import { getArticleIdForSocialMetadataOps } from '../../utils';
import { getState } from '../reducers';

const selectState = createSelector(getState, state => state.share);

export const selectArticlesBookmarks = createSelector(selectState, state => state.bookmarks);

export const selectBookmarksByArticleIdSelector = createSelector(
    selectArticlesBookmarks,
    bookmarks =>
        ({ id, primaryArticleId }: { id: IArticle['id']; primaryArticleId: IArticle['primaryArticleId'] }) => {
            const articleId = getArticleIdForSocialMetadataOps({ id, primaryArticleId });
            return bookmarks[articleId]?.bookmarks || [];
        },
);

const selectArticleIdParam = createParameterSelector<'articleId', IArticle['id']>('articleId');
const selectPrimaryArticleIdParam = createParameterSelector<'primaryArticleId', IArticle['primaryArticleId']>('primaryArticleId');

const isReadyStatus = (status: OperationStatus) => status === OperationStatus.Done || status === OperationStatus.Failed;

export const selectShouldDisableSaveToCollection = createSelector(
    selectArticlesBookmarks,
    selectUserIsAuthenticated,
    selectArticleIdParam,
    selectPrimaryArticleIdParam,
    (bookmarks, isAuthenticated, id, primaryArticleId) => {
        if (!isAuthenticated) {
            return false;
        }
        const articleId = getArticleIdForSocialMetadataOps({ id, primaryArticleId });
        const status = bookmarks[articleId]?.status ?? OperationStatus.None;
        return !isReadyStatus(status);
    },
);

export const selectSharePreviewByArticleIdSelector = createSelector(selectState, state => (articleId: string) => state.previews[articleId] || null);

export const selectArticlesProviders = createSelector(selectState, state => state.providers);

export const selectShareProvidersByArticleIdSelector = createSelector(
    selectArticlesProviders,
    map => (articleId: string) => map[articleId]?.supported || [],
);
